import { useGlobalSearchParams as useGlobalSearchParamsFromExpoRouter } from "expo-router";

/**
 * BANNED. Do not use `useGlobalSearchParams` — it reads params off the router's GLOBAL
 * (deepest-focused) route, which is shared across every mounted stack entry. When more than one
 * instance of the same dynamic route is mounted (e.g. successive learn/review cards pushed onto the
 * stack), a still-mounted previous entry's params bleed into the read, so a component reading a
 * per-route identity (`lesson`/`review`/`type`/`tab`/`morpheme`) sees a STALE value from a
 * different card. This caused the learn-card audio/footer stale-state bug.
 *
 * Read a per-route param instead from the value the FOCUSED route already threaded down:
 * - a route/layout's own `useLocalSearchParams()` (or our {@link import("./use-local-search-params.ts").useLayoutSearchParams}),
 * - a prop passed from the focused page,
 * - or a context populated above the relevant navigator (e.g. `morphemeHrefObject.params`).
 *
 * Enforced by the `no-restricted-imports` lint rule; this runtime throw is the backstop for
 * namespace access (`ExpoRouter.useGlobalSearchParams()`) that the lint rule can't see.
 *
 * Use a per-route param source (see above), never a global read.
 */
export function useGlobalSearchParams(
  ..._args: Parameters<typeof useGlobalSearchParamsFromExpoRouter>
): never {
  throw new Error(
    "useGlobalSearchParams is banned: it reads the globally-focused route's params, which bleed " +
      "across mounted stack entries and go stale on the next card. Read a per-route param instead " +
      "(useLocalSearchParams / useLayoutSearchParams, a prop from the focused page, or a context " +
      "populated above the navigator such as morphemeHrefObject.params).",
  );
}
